import { useState } from 'react';
import { supabase, PastEvent } from '../../../lib/supabase';
import ImageCropModal from './ImageCropModal';

interface PastEventModalProps {
  event: PastEvent | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function PastEventModal({ event, onClose, onSaved }: PastEventModalProps) {
  const [form, setForm] = useState({
    title: event?.title || '',
    description: event?.description || '',
    event_date: event?.event_date || '',
    location: event?.location || '',
    image_url: event?.image_url || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [cropImage, setCropImage] = useState<File | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const uploadImage = async (blob: Blob) => {
    setUploading(true);
    setError('');
    try {
      const fileName = `past_event_${Date.now()}_${Math.random().toString(36).slice(2)}.jpg`;
      const { data, error: uploadError } = await supabase.storage
        .from('Fotos')
        .upload(fileName, blob, { upsert: true, contentType: 'image/jpeg' });

      if (uploadError) {
        setError(`Error al subir la imagen: ${uploadError.message}`);
        return;
      }
      if (data) {
        const { data: urlData } = supabase.storage.from('Fotos').getPublicUrl(data.path);
        setForm(prev => ({ ...prev, image_url: urlData.publicUrl }));
      }
    } catch {
      setError('Error inesperado al subir la imagen.');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (file: File) => {
    setCropImage(file);
  };

  const handleCropConfirm = (croppedBlob: Blob) => {
    uploadImage(croppedBlob);
    setCropImage(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validación adicional
    if (!form.title.trim() || !form.event_date || !form.location.trim()) {
      setError('Por favor completa todos los campos obligatorios.');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        event_date: form.event_date,
        location: form.location.trim(),
        image_url: form.image_url.trim() || null,
      };

      if (event) {
        const { error: err } = await supabase.from('past_events').update(payload).eq('id', event.id);
        if (err) throw err;
      } else {
        const { error: err } = await supabase.from('past_events').insert(payload);
        if (err) throw err;
      }

      onSaved();
      onClose();
    } catch (err: unknown) {
      console.error('Error al guardar:', err);
      setError('Error al guardar el evento. Verifica que todos los campos estén completos.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/80 backdrop-blur-sm">
        <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-800">
            <h2 className="text-white font-bold text-lg">
              {event ? 'Editar evento pasado' : 'Nuevo evento pasado'}
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-white hover:bg-gray-800 rounded-lg transition-all cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="sm:col-span-2">
                <label className="block text-gray-400 text-xs uppercase tracking-widest mb-2">
                  Título *
                </label>
                <input
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  required
                  placeholder="Ej: Ultra Music Festival"
                  className="w-full bg-black border border-gray-700 text-white text-sm rounded-lg px-4 py-3 focus:outline-none focus:border-fuchsia-400 transition-all placeholder-gray-600"
                />
              </div>

              <div>
                <label className="block text-gray-400 text-xs uppercase tracking-widest mb-2">
                  Fecha *
                </label>
                <input
                  type="date"
                  name="event_date"
                  value={form.event_date}
                  onChange={handleChange}
                  required
                  className="w-full bg-black border border-gray-700 text-white text-sm rounded-lg px-4 py-3 focus:outline-none focus:border-fuchsia-400 transition-all"
                />
              </div>

              <div>
                <label className="block text-gray-400 text-xs uppercase tracking-widest mb-2">
                  Ciudad / Ubicación *
                </label>
                <input
                  name="location"
                  value={form.location}
                  onChange={handleChange}
                  required
                  placeholder="Ej: Miami, USA"
                  className="w-full bg-black border border-gray-700 text-white text-sm rounded-lg px-4 py-3 focus:outline-none focus:border-fuchsia-400 transition-all placeholder-gray-600"
                />
              </div>

              {/* Imagen */}
              <div className="sm:col-span-2">
                <label className="block text-gray-400 text-xs uppercase tracking-widest mb-2">
                  Imagen del evento
                </label>
                <div className="space-y-3">
                  {/* Preview */}
                  {form.image_url && (
                    <div className="w-full h-36 rounded-lg overflow-hidden border border-gray-800 relative">
                      <img src={form.image_url} alt="Preview" className="w-full h-full object-cover object-top" />
                      <button
                        type="button"
                        onClick={() => setForm(prev => ({ ...prev, image_url: '' }))}
                        className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center bg-black/70 hover:bg-red-500/80 rounded-full text-white text-xs transition-colors cursor-pointer"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                  )}
                  {/* Botón subir desde PC */}
                  <label
                    className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      uploading
                        ? 'bg-gray-800 border-gray-700 text-gray-500'
                        : 'bg-fuchsia-500/20 hover:bg-fuchsia-500/30 border-fuchsia-400/50 text-fuchsia-400'
                    }`}
                  >
                    <i className={uploading ? 'ri-loader-4-line animate-spin' : 'ri-upload-cloud-line'}></i>
                    {uploading ? 'Subiendo...' : 'Subir imagen desde PC'}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={uploading}
                      onChange={e => {
                        const f = e.target.files?.[0];
                        if (f) handleFileSelect(f);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  {/* O pegar URL */}
                  <input
                    name="image_url"
                    value={form.image_url}
                    onChange={handleChange}
                    placeholder="O pega aquí la URL de la imagen"
                    className="w-full bg-black border border-gray-700 text-white text-sm rounded-lg px-4 py-3 focus:outline-none focus:border-fuchsia-400 transition-all placeholder-gray-600"
                  />
                </div>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-gray-400 text-xs uppercase tracking-widest mb-2">
                  Descripción
                </label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  rows={3}
                  maxLength={500}
                  placeholder="Descripción del evento..."
                  className="w-full bg-black border border-gray-700 text-white text-sm rounded-lg px-4 py-3 focus:outline-none focus:border-fuchsia-400 transition-all placeholder-gray-600 resize-none"
                />
                <p className="text-gray-600 text-xs mt-1 text-right">{form.description.length}/500</p>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-red-900/30 border border-red-700/50 rounded-lg px-4 py-3">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-error-warning-line text-red-400 text-sm"></i>
                </div>
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm font-medium py-3 rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading || uploading}
                className="flex-1 bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-bold text-sm py-3 rounded-lg transition-all shadow-[0_0_15px_rgba(217,70,239,0.3)] hover:shadow-[0_0_25px_rgba(217,70,239,0.5)] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Guardando...
                  </span>
                ) : event ? (
                  'Guardar cambios'
                ) : (
                  'Crear evento'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {cropImage && (
        <ImageCropModal
          imageFile={cropImage}
          aspectRatio={16 / 9}
          onConfirm={handleCropConfirm}
          onCancel={() => {
            setUploading(false);
            setCropImage(null);
          }}
          title="Recortar imagen del evento"
        />
      )}
    </>
  );
}